import dialogStack from '@/components/evergreen/overlays/dialogStack';

const createDialogKeyListener = (dialogId, listener, modifiers) => {
	let _listener = createKeyListener(listener, modifiers);
	return event => {
		if (dialogStack.top && dialogStack.top.id === dialogId) _listener(event);
	};
};

const createKeyListener = (listener, modifiers) => (
	event => {
		if (typeof modifiers.shiftKey === 'boolean') {
			if (modifiers.shiftKey !== event.shiftKey) return;
		}
		if (typeof modifiers.ctrlKey === 'boolean') {
			if (modifiers.ctrlKey !== event.ctrlKey) return;
		}
		if (typeof modifiers.altKey === 'boolean') {
			if (modifiers.altKey !== event.altKey) return;
		}
		if (modifiers.stop) event.stopPropagation();
		if (modifiers.prevent) event.preventDefault();
		listener(event);
	}
);

// Without lowerCase some keys will trigger differently depending on capsLock state!
const getKey = (key) => key.toLowerCase();

/**
 * Register keydown-listeners for specific keys (and optionally any modifier-key combination).
 * With addDialogKeyListener one can register a listener that is only active when the dialog is.
 * The modifiers-object accepts the following values: {
 * 		shiftKey: undefined / true / false,
 * 		ctrlKey: undefined / true / false,
 * 		altKey: undefined / true / false,
 * 		stop: true / false (undefined defaults to false),
 * 		prevent: true / false (undefined defaults to false),
 * }
 * If a modifier-key is undefined the listener will be called regardless of the modifier-key state.
 * If the modifer-key is true the listener will only be called when the modifier-key is pressed.
 * If the modifier-key is false the listener will only be called if the modifier-key is not pressed.
 * If stop is true then event.stopPropagation() will be called.
 * If prevent is true then event.preventDefault() will be called.
 */
class KeydownController {
	constructor() {
		if (!KeydownController.instance) {
			// Is filled with one map for every key with listeners.
			// Each of those maps are filled with mappings originalListener => keyListener,
			// where keyListener are the result of createKeyListener.
			// The reason we have mappings from originalListeners is because we need to find the keyListener
			// to remove based on the listener that is known outside of this class (was passed to addKeyListener).
			this.listeners = new Map();

			this.createDialogKeyListener = createDialogKeyListener.bind(null, dialogStack);
			this.runListeners = this.runListeners.bind(this);
			document.addEventListener('keydown', this.runListeners);
			KeydownController.instance = this;
		}
		return KeydownController.instance;
	}

	runListeners(event) {
		let key = getKey(event.key);
		if (!this.listeners.has(key) || !this.listeners.get(key).size) return;
		this.listeners.get(key).forEach(keyListener => { keyListener(event); });
	}

	/**
	 * Register a listener that is only active when current dialog is on top of stack.
	 * @param {string} dialogId Id of current dialog.
	 * @param {string} key Key to be pressed.
	 * @param {function} listener Function to run when key is pressed.
	 * @param {object} modifiers See KeydownController class description
	 */
	addDialogKeyListener(dialogId, key, listener, modifiers={}) {
		this._addKeyListener(key, listener, createDialogKeyListener(dialogId, listener, modifiers));
	}

	/**
	 * Register a listener that gets fired when key is pressed.
	 * @param {string} key Key to be pressed.
	 * @param {function} listener Function to run when key is pressed.
	 * @param {object} modifiers See KeydownController class description
	 */
	addKeyListener(key, listener, modifiers={}) {
		this._addKeyListener(key, listener, createKeyListener(listener, modifiers));
	}

	_addKeyListener(key, originalListener, keyListener) {
		let _key = getKey(key);
		if (!this.listeners.has(_key)) this.listeners.set(_key, new Map());
		this.listeners.get(_key).set(originalListener, keyListener);
	}

	/**
	 * Remove a previously registered listener.
	 * The originalListener function must be the same reference to the same function that was
	 * passed with addKeyListener or addDialogKeyListener.
	 * @param {string} key Key to be pressed.
	 * @param {function} originalListener Function to run when key is pressed.
	 */
	removeKeyListener(key, originalListener) {
		let _key = getKey(key);
		if (!this.listeners.has(_key) || !this.listeners.get(_key).has(originalListener)) {
			console.warn(`Trying to remove unexistent listener from keydownController`);
			console.warn(key, originalListener);
		}
		this.listeners.get(_key).delete(originalListener);
	}
}

// eslint-disable-next-line
if (process.env.NODE_ENV === 'development') {
	// Let developers inspect the keydownController when running locally, in case anything weird happens.
	window.__keydownController = new KeydownController();
}

export default new KeydownController();